import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { PackagePlus, Calendar, Search, ArrowLeft, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ComplementosMateriais = () => {
  const { user } = useAuth();
  const permissoes = user?.funcao?.permissoes;
  const podeManutencao = !permissoes || !permissoes.length || permissoes.includes('relatorios_manutencao');
  const voltarHref = podeManutencao ? '/relatorios-manutencao' : '/';
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState({
    data_inicio: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0],
  });

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/complementos-materiais', {
        params: {
          data_inicio: periodo.data_inicio,
          data_fim: periodo.data_fim,
          limit: 200,
        },
      });
      setItens(data?.complementos || []);
      setTotal(data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar complementos');
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [periodo.data_inicio, periodo.data_fim]);

  const formatDate = (str) => {
    if (!str) return '—';
    try {
      return new Date(str).toLocaleString('pt-BR');
    } catch {
      return str;
    }
  };

  const m8Label = (c) => {
    if (c.m8_requisicao_id) return `Req. ${c.m8_requisicao_id}`;
    const s = c.m8_sync_status;
    if (s === 'synced') return 'Sincronizado';
    if (s === 'pending') return 'Pendente';
    if (s === 'failed') return 'Falhou';
    if (s === 'not_mapped') return 'Não mapeado';
    return '—';
  };

  const m8Badge = (c) => {
    if (c.m8_requisicao_id || c.m8_sync_status === 'synced') return 'bg-emerald-100 text-emerald-700';
    const s = (c.m8_sync_status || '').toLowerCase();
    if (s === 'pending') return 'bg-amber-100 text-amber-700';
    if (s === 'failed' || s === 'not_mapped') return 'bg-rose-100 text-rose-700';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={voltarHref} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
            <PackagePlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Complementos de material</h1>
            <p className="text-slate-600">Peças esquecidas no relatório — só acertam o estoque</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Período
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data início</label>
            <input
              type="date"
              value={periodo.data_inicio}
              onChange={(e) => setPeriodo({ ...periodo, data_inicio: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data fim</label>
            <input
              type="date"
              value={periodo.data_fim}
              onChange={(e) => setPeriodo({ ...periodo, data_fim: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 text-sm text-slate-600">
          <Search className="w-4 h-4 inline mr-1" /> {total} complemento(s)
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Documento</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Série / Cliente</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Data manutenção</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Técnico</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Peças</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">M8</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Relatório</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-500">Carregando...</td>
                </tr>
              ) : itens.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-500">
                    Nenhum complemento no período.
                  </td>
                </tr>
              ) : (
                itens.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm">
                      <div className="font-medium text-slate-900">{c.numero_documento || c.id}</div>
                      <div className="text-xs text-slate-500">{formatDate(c.created_at)}</div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="font-medium text-slate-900">{c.equipamento_numero_serie || '—'}</div>
                      <div className="text-xs text-slate-500">{c.cliente_nome || '—'}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">
                      {c.data_manutencao
                        ? c.data_manutencao.split('-').reverse().join('/')
                        : '—'}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{c.tecnico_nome || '—'}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">
                      {(c.pecas || []).map((p) => `${p.nome} (${p.quantidade})`).join(', ') || '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${m8Badge(c)}`}>
                        {m8Label(c)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {c.manutencao_id ? (
                        <Link
                          to={`/relatorios-manutencao/${c.manutencao_id}`}
                          className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                        >
                          <Eye className="w-4 h-4" /> {c.numero_relatorio || 'Ver'}
                        </Link>
                      ) : (
                        <span className="text-xs text-slate-400">Sem vínculo</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComplementosMateriais;
